import { api, ApiError } from "../api.js";
import { esc } from "../utils.js";
import { navigate } from "../app.js";

const DIFFICULTIES = ["beginner", "intermediate", "advanced", "tech"];

/** Doubles as "add a dive site" (#/create-dive-site) and "edit one"
 * (#/dive-site/:id/edit) — params.id, when present, switches this into
 * edit mode and pre-fills the form, the same shape createPost uses. */
export async function render(container, params) {
  const editingId = params?.id;
  let existing = null;

  if (editingId) {
    container.innerHTML = `<div class="text-center py-5"><div class="spinner-border text-primary"></div></div>`;
    try {
      existing = await api.get(`/dive-sites/${editingId}`);
    } catch (err) {
      container.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>Dive site not found.</div></div>`;
      return;
    }
  }

  const value = (field) => esc(existing?.[field] ?? "");

  container.innerHTML = `
    <h1 class="display-heading h3 mb-4">${editingId ? "Edit Dive Site" : "Add Dive Site"}</h1>
    <form id="site-form">
      <div class="mb-3">
        <label class="form-label" for="site-name">Name</label>
        <input type="text" class="form-control" id="site-name" required value="${value("name")}" />
      </div>
      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label" for="site-lat">Latitude</label>
          <input type="number" step="any" class="form-control" id="site-lat" required value="${value("latitude")}" />
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label" for="site-lng">Longitude</label>
          <input type="number" step="any" class="form-control" id="site-lng" required value="${value("longitude")}" />
        </div>
      </div>
      <div class="form-text mb-3">Tip: search "[site name] coordinates" to find lat/long quickly.</div>
      <div class="mb-3">
        <label class="form-label" for="site-city">City (optional)</label>
        <input type="text" class="form-control" id="site-city" value="${value("city")}" />
      </div>
      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label" for="site-state">State / Province (optional)</label>
          <input type="text" class="form-control" id="site-state" value="${value("state_province")}" />
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label" for="site-country">Country (optional)</label>
          <input type="text" class="form-control" id="site-country" value="${value("country")}" />
        </div>
      </div>
      <div class="mb-3">
        <label class="form-label" for="site-ocean">Ocean or sea (optional)</label>
        <input type="text" class="form-control" id="site-ocean" value="${value("ocean")}" />
      </div>
      <div class="mb-3">
        <label class="form-label" for="site-difficulty">Difficulty (optional)</label>
        <select class="form-select" id="site-difficulty">
          <option value="">None</option>
          ${DIFFICULTIES.map((d) => `<option value="${d}"${existing?.difficulty === d ? " selected" : ""}>${d}</option>`).join("")}
        </select>
      </div>
      <div class="mb-3">
        <label class="form-label" for="site-depth">Max depth in meters (optional)</label>
        <input type="number" step="any" min="0" class="form-control" id="site-depth" value="${value("max_depth_m")}" />
      </div>
      <div class="mb-3">
        <label class="form-label" for="site-description">Description (optional)</label>
        <textarea class="form-control" id="site-description" rows="3">${value("description")}</textarea>
      </div>
      <div class="mb-3">
        <label class="form-label" for="site-notes">Notes (optional)</label>
        <textarea class="form-control" id="site-notes" rows="6">${value("notes")}</textarea>
        <div class="form-text">Entries, routes, hazards: anything a diver should know before going.</div>
      </div>
      <div id="site-error" class="text-danger small mb-3 d-none"></div>
      <div class="d-flex gap-2">
        <button type="submit" class="btn btn-primary" id="site-submit">${editingId ? "Save Changes" : "Save"}</button>
        <button type="button" class="btn btn-outline-secondary" id="site-cancel">Cancel</button>
      </div>
    </form>`;

  container.querySelector("#site-cancel").addEventListener("click", () => history.back());

  container.querySelector("#site-form").addEventListener("submit", async (e) => {
    e.preventDefault();
    const errorEl = container.querySelector("#site-error");
    errorEl.classList.add("d-none");
    const submitBtn = container.querySelector("#site-submit");
    submitBtn.disabled = true;

    try {
      const depthVal = container.querySelector("#site-depth").value;
      const body = {
        name: container.querySelector("#site-name").value.trim(),
        description: container.querySelector("#site-description").value.trim() || null,
        latitude: Number(container.querySelector("#site-lat").value),
        longitude: Number(container.querySelector("#site-lng").value),
        city: container.querySelector("#site-city").value.trim() || null,
        state_province: container.querySelector("#site-state").value.trim() || null,
        country: container.querySelector("#site-country").value.trim() || null,
        ocean: container.querySelector("#site-ocean").value.trim() || null,
        notes: container.querySelector("#site-notes").value.trim() || null,
        difficulty: container.querySelector("#site-difficulty").value || null,
        max_depth_m: depthVal ? Number(depthVal) : null,
      };
      const site = editingId
        ? await api.patch(`/dive-sites/${editingId}`, body)
        : await api.post("/dive-sites", body);
      navigate(`/dive-site/${site.id}`);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : "Something went wrong.";
      errorEl.classList.remove("d-none");
    } finally {
      submitBtn.disabled = false;
    }
  });
}
